import React, { forwardRef, useState, Ref } from 'react'
import { connect, useSelector } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import clsx from 'clsx'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { RootActionType, RootState } from 'duck'
import { actions, selectors, FormDataType } from '../../duck'
import { ReduxProps } from 'utils/Types'
import Tooltip, { TooltipProps } from 'components/Tooltip'
import TextField, { TextFieldProps } from 'components/TextField'
import TextFieldNumber, { TextFieldNumberProps } from 'components/TextField/TextFieldNumber'

import styles from '../NFTSellForm.module.scss'

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      updateFormData: actions.updateFormData,
      updateFormError: actions.updateFormError
    },
    dispatch
  )

type TextFieldItemProps = ReduxProps<typeof mapDispatchToProps> &
  Pick<TextFieldProps, 'className' | 'label' | 'multiline' | 'type' | 'InputProps'> & {
    hide?: boolean
    formDataType: keyof FormDataType
    tooltipData?: Pick<TooltipProps, 'className' | 'classes' | 'title'>
  }

const Textarea = forwardRef((props: TextFieldProps, ref: Ref<any>) => {
  const {
    className,
    color,
    error,
    helperText,
    type,
    label,
    value,
    variant,
    InputProps,
    disabled,
    onBlur,
    onChange,
    onFocus
  } = props

  const [isFocused, setFocus] = useState(typeof value !== 'undefined' && value !== '')

  return (
    <TextField
      ref={ref}
      type={type}
      color={color}
      variant={variant}
      className={className}
      label={label}
      value={value}
      onBlur={event => {
        onBlur?.(event)
        setFocus(false)
      }}
      onChange={onChange}
      onFocus={event => {
        onFocus?.(event)
        setFocus(true)
      }}
      error={error}
      helperText={helperText}
      minRows={isFocused || value ? 8 : undefined}
      maxRows={8}
      multiline
      disabled={disabled}
      fullWidth
      InputProps={{
        ...InputProps,
        classes: {
          ...InputProps?.classes,
          input: clsx(styles.TextareaInput, InputProps?.classes?.input)
        }
      }}
    />
  )
})

const TextFieldComponent = forwardRef((props: TextFieldProps, ref: Ref<any>) => {
  const {
    className,
    error,
    helperText,
    label,
    multiline,
    InputProps,
    type,
    value,
    disabled,
    onBlur,
    onChange,
    onFocus
  } = props

  if (multiline)
    return (
      <Textarea
        ref={ref}
        type={type}
        color="secondary-alt"
        variant="filled"
        className={className}
        label={label}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
        onFocus={onFocus}
        error={error}
        helperText={helperText}
        fullWidth
        InputProps={InputProps}
        disabled={disabled}
      />
    )

  if (type === 'number') {
    return (
      <TextFieldNumber
        ref={ref}
        color="secondary-alt"
        variant="filled"
        className={className}
        label={label}
        value={value}
        onBlur={onBlur}
        onChange={onChange as TextFieldNumberProps['onChange']}
        InputProps={InputProps}
        onFocus={onFocus}
        error={error}
        helperText={helperText}
        min={0}
        scale={999999999} // decimal
        fullWidth
        disabled={disabled}
      />
    )
  }

  return (
    <TextField
      ref={ref}
      type={type}
      color="secondary-alt"
      variant="filled"
      className={className}
      label={label}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      InputProps={InputProps}
      onFocus={onFocus}
      error={error}
      helperText={helperText}
      fullWidth
      disabled={disabled}
    />
  )
})

const TextFieldWithTooltipComponent: React.FC<
  TextFieldProps & Pick<TextFieldItemProps, 'tooltipData' | 'label'>
> = props => {
  const {
    onChange,
    className,
    error,
    helperText,
    label,
    multiline,
    disabled,
    InputProps,
    tooltipData,
    type,
    value
  } = props
  const [isFocused, setFocus] = useState(false)

  if (!tooltipData) return null

  return (
    <div className={className}>
      <Tooltip
        open={isFocused}
        classes={tooltipData?.classes}
        className={tooltipData?.className}
        title={tooltipData?.title}
        placement="bottom-start"
      >
        <TextFieldComponent
          type={type}
          color="secondary-alt"
          variant="filled"
          label={
            <span className="flex items-center">
              <span>{label}</span>{' '}
              <HelpOutlineIcon className="pointer-events-auto ml-1 text-base" />
            </span>
          }
          value={value}
          onBlur={() => {
            setFocus(false)
          }}
          onChange={onChange}
          onFocus={() => {
            setFocus(true)
          }}
          disabled={disabled}
          InputProps={InputProps}
          error={error}
          helperText={helperText}
          multiline={multiline}
        />
      </Tooltip>
    </div>
  )
}

const TextFieldItem: React.FC<TextFieldItemProps> = props => {
  const {
    updateFormError,
    updateFormData,
    hide,
    className,
    type,
    label,
    tooltipData,
    formDataType,
    multiline,
    InputProps
  } = props

  const value = useSelector((state: RootState) => selectors.formData(state)[formDataType])
  const formError = useSelector((state: RootState) => selectors.formErrors(state)[formDataType])
  const disabled = useSelector((state: RootState) => selectors.isResubmitForm(state))

  return hide ? null : tooltipData ? (
    <TextFieldWithTooltipComponent
      tooltipData={tooltipData}
      InputProps={InputProps}
      label={label}
      type={type}
      className={className}
      value={`${value ?? ''}`}
      onChange={value => {
        updateFormData({ [formDataType]: value.target.value })
        updateFormError({ [formDataType]: undefined })
      }}
      disabled={disabled}
      error={Boolean(formError)}
      helperText={formError}
      multiline={multiline}
    />
  ) : (
    <TextFieldComponent
      type={type}
      InputProps={InputProps}
      color="secondary-alt"
      variant="filled"
      className={className}
      label={label}
      value={`${value ?? ''}`}
      onChange={value => {
        updateFormData({ [formDataType]: value.target.value })
        updateFormError({ [formDataType]: undefined })
      }}
      disabled={disabled}
      error={Boolean(formError)}
      helperText={formError}
      multiline={multiline}
    />
  )
}

export default connect(null, mapDispatchToProps)(TextFieldItem)
